import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import { makeStyles } from '@mui/styles';
import DataTable from '../../../sharedModules/dataTable/DataTable/DataTable';
import axios from '../../../sharedModules/utils/axios.utils';
import ChillerJobsFilter from '../components/ChillerJobsFilter';
import CreateChillerJobModal from '../components/CreateChillerJobModal';
import { format, addDays, isFriday } from 'date-fns';
import { useLocation } from '../../contexts/LocationContext';
import LocationBadge from '../../components/LocationBadge';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    width: '100%',
    padding: 0,
  },
  box: {
    width: '100%',
  },
  content: {
    marginLeft: '0px',
    padding: '80px 30px',
  },
}));

interface UpcomingJobsProps {
  role: string;
}

const today = new Date();
const futureDate = addDays(today, isFriday(today) ? 3 : 1);

const defaultFilterValues = {
  user_id_eq: 0,
  chiller_jobs_scheduled_date_gteq: format(today, 'yyyy-MM-dd'),
  chiller_jobs_scheduled_date_lteq: format(futureDate, 'yyyy-MM-dd'),
  status_eq: 'not_started',
  chiller_jobs_company_name_eq: 'all',
};

const UpcomingJobs: React.FunctionComponent<UpcomingJobsProps> = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const { selectedLocation } = useLocation();

  const [createJobModalOpen, setCreateJobModalOpen] = useState<boolean>(false);
  const [chillerJobs, setChillerJobs] = useState<any>([]);
  const [chillerClientOptions, setChillerClientOptions] = useState<any>([]);
  const [filterValues, setFilterValues] = useState<any>(defaultFilterValues);

  const calculateStatusRowColor = (row) => {
    if (row.status.toLowerCase() === 'overdue' || row.status.toLowerCase() === 'closed no log') {
      return 'white';
    } else if (row.status.toLowerCase() === 'in_progress' || row.status.toLowerCase() === 'in progress') {
      return 'red';
    } else {
      return 'green';
    }
  };

  const upcomingJobsColumns = [
    {
      name: 'Scheduled',
      selector: (row: any) => row.scheduled_date,
      sortable: true,
    },
    {
      name: 'Order',
      selector: (row: any) => row.order_no,
      sortable: true,
    },
    {
      name: 'Customer',
      selector: 'company_name',
      sortable: true,
    },
    {
      name: 'Address',
      selector: (row: any) => row.address,
      grow: 2,
      sortable: true,
    },
    {
      name: 'Asset',
      selector: (row: any) => row.chlr_no,
      sortable: true,
    },
    {
      name: 'Department',
      selector: (row: any) => row.dept_name,
      sortable: true,
    },
    {
      name: 'Logging Tech',
      selector: (row: any) => row.display_emp_id,
      sortable: true,
    },
    {
      name: 'Status',
      selector: (row: any) => row.status,
      sortable: true,
      cell: (row: Record<string, string>) => (
        <div
          style={{
            fontWeight: 700,
            textTransform: 'uppercase',
            color: calculateStatusRowColor(row),
          }}
        >
          {row.status.replace('CLOSED NO LOG', 'Closed - No Log')}
        </div>
      ),
    },
  ];

  const fetchChillerJobs = async (fetchFilterValues) => {
    try {
      const response = await axios.get<string, any>('/chiller_logs/chiller_jobs', {
        params: {
          'ransack[user_id_eq]': fetchFilterValues.user_id_eq,
          'ransack[chiller_jobs_scheduled_date_gteq]': fetchFilterValues.chiller_jobs_scheduled_date_gteq,
          'ransack[chiller_jobs_scheduled_date_lteq]': fetchFilterValues.chiller_jobs_scheduled_date_lteq,
          'ransack[status_eq]': fetchFilterValues.status_eq,
          'ransack[chiller_jobs_order_no_eq]': fetchFilterValues.chiller_jobs_order_no_eq,
          'ransack[chiller_jobs_company_name_eq]': fetchFilterValues.chiller_jobs_company_name_eq,
          location: selectedLocation,
        },
      });
      setChillerJobs(response?.data?.result);
      setChillerClientOptions(response?.data?.client_options);
    } catch (error) {
      console.error(error);
    }
  };

  const handleImportSuccess = (orderNo: string) => {
    const newFilterValues = {
      ...filterValues,
      chiller_jobs_order_no_eq: orderNo,
    };
    setFilterValues(newFilterValues);
    sessionStorage.setItem('chillerFilterValues', JSON.stringify(newFilterValues));
    fetchChillerJobs(newFilterValues);
  };

  const conditionalRowStyles = [
    {
      when: (row) => row.status.toLowerCase() === 'overdue' || row.status.toLowerCase() === 'closed no log',
      style: {
        backgroundColor: 'rgba(242, 38, 19, 0.9)',
        color: 'white',
        '&:hover': {
          cursor: 'not-allowed',
        },
      },
    },
  ];

  useEffect(() => {
    try {
      const storedFilters = sessionStorage.getItem('chillerFilterValues');
      let initialFilterValues = defaultFilterValues;
      if (storedFilters) {
        let parsedFilters = JSON.parse(storedFilters);
        setFilterValues(parsedFilters);
        initialFilterValues = parsedFilters;
      }
      fetchChillerJobs(initialFilterValues);
    } catch {
      fetchChillerJobs(defaultFilterValues);
    }
  }, []);

  return (
    <Grid container>
      <CreateChillerJobModal
        fetchChillerJobs={() => fetchChillerJobs(filterValues)}
        onImportSuccess={handleImportSuccess}
        createJobModalOpen={createJobModalOpen}
        setCreateJobModalOpen={setCreateJobModalOpen}
      />
      <Grid item xs={12} style={{ marginTop: 20 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item style={{ marginBottom: 3 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div>
                <Typography style={{ display: 'inline-block', fontSize: '20px' }} variant="h3">
                  Chiller Logs
                </Typography>
                <NavigateNextIcon
                  fontSize="small"
                  style={{
                    color: '#3d5a80 !important',
                    fontWeight: 700,
                    fontSize: '20px !important',
                    margin: '-4px 4px',
                  }}
                />
                <Typography style={{ display: 'inline-block', fontSize: '20px' }} variant="h3">
                  Work Orders
                </Typography>
              </div>
              <LocationBadge />
            </div>
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" onClick={() => setCreateJobModalOpen(true)}>
              Import Work Order
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} style={{ marginTop: 20 }}>
        <ChillerJobsFilter
          filterOptions={{
            client_options: chillerClientOptions,
          }}
          filterValues={filterValues}
          setFilterValues={setFilterValues}
          onFilter={fetchChillerJobs}
        />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <DataTable
              title=""
              columns={upcomingJobsColumns}
              data={chillerJobs}
              onRowClicked={(row: any) => history.push(`/chiller_logs/${row.chiller_log_id}`)}
              conditionalRowStyles={conditionalRowStyles}
              dense
              highlightOnHover
              pointerOnHover
              pagination
              paginationPerPage={25}
              paginationRowsPerPageOptions={[25, 50, 100]}
              noHeader
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default UpcomingJobs;
